import React, { useState, useEffect, useMemo, useRef } from "react";
import { MapRef, Marker, Popup, useMap } from "react-map-gl/maplibre";
import Supercluster, { ClusterProperties, PointFeature } from "supercluster";
import { CompanyDTO, CompanyOptimizedDTO, EducationalOrganizationsDTO, EducationalOrganizationsOptimizedDTO } from "../../types";
import Point from "../common/point";
import { OrganizationCard } from "../modals/organization-card";
import { OrganizationClusterCard } from "../modals/organization-cluster-card";
import { ENTITY_TYPE, HoveredComp, HoveredOrg, SelectedCluster, SelectedComp, SelectedOrg } from "../../types/marker-types";
import { OrganizationName } from "../modals/organization-name";
import { CompanyCard } from "../modals/company-card";
import { getEntityTypeByOrganizationType } from "../../utils/organization.utils";
import { useKeyDown } from "../../hooks";
import { RMR_INFO } from "../../constants/contact-info";
import { RMRCard } from "../modals/rmr-card";
import { RMRInfo } from "../../types/rmr-info.dto";

type PointProperties = {
    cluster: boolean;
    organizationId?: string;
    companyId?: string;
    rmrId?: string;
    entityType?: ENTITY_TYPE;
};
interface CustomClusterProperties extends ClusterProperties {
    orgCount: number;
    compCount: number;
}

type ClusterFeature = PointFeature<CustomClusterProperties>;
type ClusterOrPoint = ClusterFeature | PointFeature<PointProperties>;

interface MapContentProps {
    rmrIsOpen: boolean;
    setRmrIsOpen: (value: boolean) => void;
    rmrShouldNavigate: boolean;
    setRmrShouldNavigate: (value: boolean) => void;
    rmrIsVisible: boolean;
    setRmrIsVisible: (value: boolean) => void;

    organizations: EducationalOrganizationsOptimizedDTO[];
    selectedOrg: SelectedOrg | null;
    setSelectedOrg: (org: SelectedOrg | null) => void;
    hoveredOrg: HoveredOrg | null;
    setHoveredOrg: (org: HoveredOrg | null) => void;

    companies: CompanyOptimizedDTO[];
    selectedComp: SelectedComp | null;
    setSelectedComp: (comp: SelectedComp | null) => void;
    hoveredComp: HoveredComp | null;
    setHoveredComp: (comp: HoveredComp | null) => void;

    selectedCluster: SelectedCluster | null;
    setSelectedCluster: (cluster: SelectedCluster | null) => void;
    mapRef: React.RefObject<MapRef | null>;
};

export const MapContent: React.FC<MapContentProps> = ({
    rmrIsOpen,
    setRmrIsOpen,
    rmrShouldNavigate,
    setRmrShouldNavigate,
    rmrIsVisible,
    setRmrIsVisible,

    organizations,
    selectedOrg,
    setSelectedOrg,
    hoveredOrg,
    setHoveredOrg,

    companies,
    selectedComp,
    setSelectedComp,
    hoveredComp,
    setHoveredComp,

    selectedCluster,
    setSelectedCluster,
    mapRef,
}) => {
    useKeyDown((e) => {
        setSelectedOrg(null);
        setSelectedComp(null);
        setSelectedCluster(null);
        setRmrIsOpen(false);

        e.preventDefault();
        e.stopPropagation();
    }, ['Escape'])

    const { current: map } = useMap();
    const [orgAnchor, setOrgAnchor] = useState<"top" | "center" | "bottom">("top");
    const [clusterAnchor, setClusterAnchor] = useState<"top" | "center" | "bottom">("top");
    const [compAnchor, setCompAnchor] = useState<"top" | "center" | "bottom">("top");
    const [rmrAnchor, setRmrAnchor] = useState<"top" | "center" | "bottom">("top");
    const [rmrIsHovered, setRmrIsHovered] = useState<boolean>(false);

    const [viewState, setViewState] = useState({
        bounds: map?.getBounds()?.toArray().flat() as
            | [number, number, number, number]
            | undefined,
        zoom: map?.getZoom() ?? 10,
    });

    const orgPopupRef = useRef<maplibregl.Popup | null>(null);
    const clusterPopupRef = useRef<maplibregl.Popup | null>(null);
    const compPopupRef = useRef<maplibregl.Popup | null>(null);
    const rmrPopupRef = useRef<maplibregl.Popup | null>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if(hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setClusterAnchor("top");
        setOrgAnchor("top");
        setCompAnchor("top");
        setRmrAnchor("top");
    }, [selectedOrg, selectedCluster, selectedComp, rmrIsOpen]);

    useEffect(() => {
        if(!map) return;

        const updateViewState = () => {
            setViewState({
                bounds: map.getBounds()?.toArray().flat() as
                    | [number, number, number, number]
                    | undefined,
                zoom: map.getZoom() ?? 10,
            });
        };

        updateViewState();
        map.on("move", updateViewState);
        map.on("zoom", updateViewState);

        return () => {
            map.off("move", updateViewState);
            map.off("zoom", updateViewState);
        };
    }, [map]);

    useEffect(() => {
        if(rmrIsOpen && rmrShouldNavigate) {
            handleSelect(undefined, undefined);
        }

        if(!rmrIsVisible) setRmrIsVisible(true);
        if(rmrIsOpen && rmrIsHovered) setRmrIsHovered(false);
    }, [rmrIsOpen, rmrShouldNavigate]);

    const { bounds, zoom } = viewState;

    const points: PointFeature<{ 
        cluster: boolean; 
        organizationId?: string; 
        companyId?: string; 
        rmrId?: string;
        entityType?: ENTITY_TYPE; 
    }>[] =
        useMemo(() => {
            const orgPoints = organizations.map((org) => {
                return {
                    type: "Feature" as const,
                    properties: {
                        cluster: false,
                        organizationId: org.id,
                        entityType: getEntityTypeByOrganizationType(org.type),
                    },
                    geometry: {
                        type: "Point" as const,
                        coordinates: [org.longitude, org.latitude],
                    },
                };
            });

            const compPoints = companies.map((comp) => ({
                type: "Feature" as const,
                properties: {
                    cluster: false,
                    companyId: comp.id,
                    entityType: ENTITY_TYPE.COMPANY as const,
                },
                geometry: {
                    type: "Point" as const,
                    coordinates: [comp.longitude, comp.latitude],
                },
            }));

            const rmrPoint = {
                type: "Feature" as const,
                properties: {
                    cluster: false,
                    rmrId: RMR_INFO.id,
                    entityType: ENTITY_TYPE.RMR as const,
                },
                geometry: {
                    type: "Point" as const,
                    coordinates: [RMR_INFO.longitude, RMR_INFO.latitude],
                },
            };

            return [...orgPoints, ...compPoints, ...(rmrIsVisible ? [rmrPoint] : [])];
        }, [organizations, companies, rmrIsVisible, rmrShouldNavigate]);

    const { clusters, supercluster } = useMemo(() => {
        const clusterer = new Supercluster<PointProperties, PointProperties>({
            radius: 35,
            maxZoom: 16,
        });
        clusterer.load(points);

        if(!bounds) return { clusters: [], supercluster: clusterer }

        const rawClusters = clusterer.getClusters(bounds, Math.floor(zoom));
        const enrichedClusters = rawClusters.map((cluster) => {
            if(!cluster.properties.cluster) {
                return cluster;
            }

            if(typeof cluster.id !== 'number') return cluster;

            const leaves = clusterer.getLeaves(cluster.id, Infinity);
            let orgCount = 0;
            let compCount = 0;

            for(const leaf of leaves) {
                if(leaf.properties.entityType === 'company') compCount++;
                else if(leaf.properties.entityType !== ENTITY_TYPE.RMR) orgCount++;
            }

            return {
                ...cluster,
                properties: {
                    ...cluster.properties,
                    orgCount,
                    compCount,
                }
            };
        });

        return {
            clusters: enrichedClusters,
            supercluster: clusterer,
        };
    }, [points, bounds, zoom, rmrIsOpen, rmrIsVisible]);

    useEffect(() => {
        if(rmrShouldNavigate || !rmrIsVisible) return;

        const rmrIsClustered = clusters.some((cluster) => {
            if(!cluster.properties.cluster || typeof cluster.id !== "number") return false;

            return supercluster
                .getLeaves(cluster.id, Infinity)
                .some((leaf) => leaf.properties.rmrId === RMR_INFO.id);
        });

        if(rmrIsClustered) {
            setRmrIsVisible(false);
        }
    }, [clusters, rmrIsVisible, rmrShouldNavigate, supercluster]);

    const orgMap = useMemo(() => {
        const map = new Map<string, EducationalOrganizationsOptimizedDTO>();
        organizations.forEach((o) => map.set(o.id, o));

        return map;
    }, [organizations]);
    const compMap = useMemo(() => {
        const comp = new Map<string, CompanyOptimizedDTO>();
        companies.forEach((c) => comp.set(c.id, c));

        return comp;
    }, [companies]);

    const handleSelect = (orgId?: string, compId?: string) => {
        let org: EducationalOrganizationsOptimizedDTO | undefined;
        let comp: CompanyOptimizedDTO | undefined;
        let rmr: RMRInfo | undefined;

        if(orgId) {
            org = orgMap.get(orgId);
        } else if(compId) {
            comp = compMap.get(compId);
        } else if(rmrIsOpen) {
            rmr = RMR_INFO;
        }

        if((!org && !comp && !rmr) || !map) return;

        setSelectedCluster(null);
        setSelectedOrg(null);
        setSelectedComp(null);

        const popupHeight = window.innerHeight * 0.5;
        const padding = 60;
        const offsetY = -(popupHeight / 2 + padding);

        map.flyTo({
            center: [
                org ? org.longitude 
                : comp ? comp.longitude
                : rmr!.longitude,
                org ? org.latitude 
                : comp ? comp.latitude
                : rmr!.latitude,
            ],
            zoom: 15,
            offset: [0, offsetY],
            duration: 800,
            essential: true,
        });

        map.once("moveend", () => {
            if(org) {
                setSelectedOrg({
                    ...org,
                    longitude: org.longitude,
                    latitude: org.latitude,
                });
            } else if(comp) {
                setSelectedComp({
                    ...comp!,
                    longitude: comp!.longitude,
                    latitude: comp!.latitude,
                });
            } else if(rmrIsOpen) {
                setRmrIsOpen(true);
                setRmrShouldNavigate(false);
            }
        });
    };

    useEffect(() => {
        if(!map) return;

        const popup = 
            selectedOrg ? orgPopupRef.current
            : selectedComp ? compPopupRef.current
            : selectedCluster ? clusterPopupRef.current
            : rmrIsOpen ? rmrPopupRef.current : null;

        if(!popup) return;

        const popupEl = popup.getElement();
        const mapContainer = map.getContainer();
        const maxBounds = map.getMaxBounds();

        if(!popupEl || !mapContainer || !maxBounds) return;

        const lngLat = popup.getLngLat();
        const popupHeight = popupEl.getBoundingClientRect().height;

        const projected = map.project(lngLat);

        const bottomPoint = {
            x: projected.x,
            y: projected.y + popupHeight,
        };

        const topPoint = {
            x: projected.x,
            y: projected.y - popupHeight,
        };

        const bottomLngLat = map.unproject([bottomPoint.x, bottomPoint.y]);
        const topLngLat = map.unproject([topPoint.x, topPoint.y]);

        const exceedsBottomBounds = bottomLngLat.lat < maxBounds.getSouth();
        const exceedsTopBounds = topLngLat.lat > maxBounds.getNorth();

        let newAnchor: "top" | "center" | "bottom" = "top";

        if(exceedsBottomBounds) {
            if(exceedsTopBounds) {
                newAnchor = "center";
            } else {
                newAnchor = "bottom";
            }
        }

        if(selectedOrg) {
            setOrgAnchor(newAnchor);
        } else if(selectedCluster) {
            setClusterAnchor(newAnchor);
        } else if(selectedComp) {
            setCompAnchor(newAnchor);
        } else if(rmrIsOpen) {
            setRmrAnchor(newAnchor);
        }

        setTimeout(() => {
            if(!map) return;

            const popup = 
                selectedOrg ? orgPopupRef.current 
                : selectedComp ? compPopupRef.current 
                : selectedCluster ? clusterPopupRef.current 
                : rmrIsOpen ? rmrPopupRef.current : null;

            if(!popup || (popup === rmrPopupRef.current && rmrShouldNavigate)) return;

            const popupEl = popup.getElement();
            const mapContainer = map.getContainer();
            const maxBounds = map.getMaxBounds();

            if(!popupEl || !mapContainer || !maxBounds) return;
            const popupRect = popupEl.getBoundingClientRect();
            const mapRect = mapContainer.getBoundingClientRect();

            let dx = 0;
            let dy = 0;

            if(popupRect.left < mapRect.left) {
                dx = popupRect.left - mapRect.left;
            }

            if(popupRect.right > mapRect.right) {
                dx = popupRect.right - mapRect.right;
            }

            if(popupRect.top < mapRect.top) {
                dy = popupRect.top - mapRect.top - 40;
            }

            if(popupRect.bottom > mapRect.bottom) {
                dy = popupRect.bottom - mapRect.bottom + 35;
            }

            if(dx !== 0 || dy !== 0) {
                const center = map.getCenter();
                const projectedCenter = map.project(center);

                const newCenter = map.unproject([
                    projectedCenter.x + dx,
                    projectedCenter.y + dy
                ]);

                map.easeTo({
                    center: newCenter,
                    duration: 300
                });
            }
        }, 0);
    }, [selectedOrg, selectedCluster, selectedComp, rmrIsOpen, map]);

    return (
        <>
            {clusters.map((cluster: ClusterOrPoint | undefined) => {
                if(!cluster) return cluster;

                const [lng, lat] = cluster.geometry.coordinates;
                const { cluster: isCluster, point_count: pointCount, orgCount, compCount, cluster_id } = (cluster.properties as CustomClusterProperties);

                return (
                    <Marker
                        key={cluster.id || `${lng}-${lat}`}
                        longitude={lng}
                        latitude={lat}
                        anchor="bottom"
                        offset={[0, -6]}
                    >
                        <div
                            onMouseEnter={(e) => {
                                if(isCluster) return;

                                const props = cluster.properties as PointProperties;
                                let handler: () => void;

                                if(props.organizationId) {
                                    const orgId = props.organizationId;
                                    const org = orgMap.get(orgId);
                                    if(!org || org.id === selectedOrg?.id) return;

                                    handler = () => {
                                        setHoveredOrg({
                                            ...org,
                                            longitude: lng,
                                            latitude: lat
                                        });
                                    }
                                } else if(props.companyId) {
                                    const compId = props.companyId;
                                    const comp = compMap.get(compId);
                                    if(!comp || comp.id === selectedComp?.id) return;

                                    handler = () => {
                                        setHoveredComp({
                                            ...comp,
                                            longitude: lng,
                                            latitude: lat
                                        });
                                    }
                                } else if(props.rmrId) {
                                    if(rmrIsOpen) return;

                                    handler = () => setRmrIsHovered(true);
                                } else { return console.dir('Props doesn`t have valid ID: ' + props, {depth: null}); }

                                hoverTimeoutRef.current = setTimeout(() => {
                                    handler();
                                }, 350);
                            }}
                            onMouseLeave={(e) => {
                                if(isCluster) return;
                                const isOrg = !!(cluster.properties as PointProperties).organizationId;
                                const isCompany = !!(cluster.properties as PointProperties).companyId

                                if(hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                    hoverTimeoutRef.current = null;
                                }

                                if(isOrg) setHoveredOrg(null);
                                else if(isCompany) setHoveredComp(null);
                                else setRmrIsHovered(false);
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if(isCluster) {
                                    const leaves = supercluster.getLeaves(cluster_id, Infinity);
                                    let clusterHasOrg = false;
                                    let clusterHasComp = false;
                                    const entitiesInfo = leaves
                                        .map((leaf) => {
                                            const orgId = leaf.properties.organizationId;
                                            const compId = leaf.properties.companyId;
                                            const rmrId = leaf.properties.rmrId;

                                            if(orgId) {
                                                const org = orgMap.get(orgId);
                                                if(!org) return null;

                                                clusterHasOrg = true;

                                                return {
                                                    name: org.shortName || org.id || "Неизвестно",
                                                    entityType: getEntityTypeByOrganizationType(org.type),
                                                    id: org.id,
                                                };
                                            } else if(compId) {
                                                const comp = compMap.get(compId);
                                                if(!comp) return null;

                                                clusterHasComp = true;

                                                return {
                                                    name: comp.name || comp.id || "Неизвестно",
                                                    entityType: ENTITY_TYPE.COMPANY,
                                                    id: comp.id,
                                                };
                                            } else if(rmrId) {
                                                return;
                                            } else {
                                                console.warn('Props doesn`t have valid ID: ' + leaf.properties);

                                                return null;
                                            }
                                        })
                                        .filter((item): item is { name: string; id: string; entityType: ENTITY_TYPE; } => item !== null);

                                    setSelectedCluster({
                                        id: cluster_id,
                                        longitude: lng,
                                        latitude: lat,
                                        count: pointCount,
                                        isMixed: clusterHasComp && clusterHasOrg,
                                        entitiesInfo
                                    });
                                    setSelectedOrg(null);
                                    setSelectedComp(null);
                                } else {
                                    const props = cluster.properties as PointProperties;
                                    const isOrg = !!props.organizationId;
                                    const isCompany = !!props.companyId;
                                    const isRmr = !!props.rmrId;

                                    if(isOrg) {
                                        const orgId = props.organizationId;
                                        const org = orgMap.get(orgId!);
                                        if(!org) return;

                                        setSelectedOrg({ ...org, longitude: lng, latitude: lat });
                                        setSelectedComp(null);
                                        setSelectedCluster(null);
                                        setRmrIsOpen(false);
                                    } else if(isCompany) {
                                        const compId = props.companyId;
                                        const comp = compMap.get(compId!);
                                        if(!comp) return;

                                        setSelectedComp({ ...comp, longitude: lng, latitude: lat });
                                        setSelectedOrg(null);
                                        setSelectedCluster(null);
                                        setRmrIsOpen(false);
                                    } else if(isRmr) {
                                        setRmrShouldNavigate(false);
                                        setRmrIsOpen(true);
                                        setSelectedComp(null);
                                        setSelectedOrg(null);
                                        setSelectedCluster(null);
                                    }
                                }
                            }}
                        >
                            <Point
                                zoom={zoom}
                                orgCount={orgCount}
                                compCount={compCount}
                                isMixed={orgCount > 0 && compCount > 0}
                                isCompany={(compCount > 0 && orgCount === 0) || (!isCluster && (cluster.properties as PointProperties).companyId)}
                                isRMR={!isCluster && (cluster.properties as PointProperties).rmrId}
                            />
                        </div>
                    </Marker>
                );
            })}

            {selectedOrg && (
                <Popup
                    longitude={selectedOrg.longitude}
                    latitude={selectedOrg.latitude}
                    anchor={orgAnchor}
                    offset={10}
                    closeButton={false}
                    closeOnClick={false}
                    focusAfterOpen={false}
                    onClose={() => setSelectedOrg(null)}
                    maxWidth="550px"
                    ref={orgPopupRef}
                    style={{ zIndex: 1 }}
                >
                    <OrganizationCard
                        optimizedOrganization={selectedOrg}
                        onClose={() => setSelectedOrg(null)}
                    />
                </Popup>
            )}

            {selectedComp && (
                <Popup
                    longitude={selectedComp.longitude}
                    latitude={selectedComp.latitude}
                    anchor={compAnchor}
                    offset={10}
                    closeButton={false}
                    closeOnClick={false}
                    focusAfterOpen={false}
                    onClose={() => setSelectedComp(null)}
                    maxWidth="550px"
                    ref={compPopupRef}
                    style={{ zIndex: 1 }}
                >
                    <CompanyCard
                        optimizedCompany={selectedComp}
                        onClose={() => setSelectedComp(null)}
                    />
                </Popup>
            )}

            {(rmrIsOpen && !rmrShouldNavigate && rmrIsVisible) && (
                <Popup
                    longitude={RMR_INFO.longitude}
                    latitude={RMR_INFO.latitude}
                    anchor={rmrAnchor}
                    offset={10}
                    closeButton={false}
                    closeOnClick={false}
                    focusAfterOpen={false}
                    onClose={() => setRmrIsOpen(false)}
                    maxWidth="550px"
                    ref={rmrPopupRef}
                    style={{ zIndex: 1 }}
                >
                    <RMRCard 
                        onClose={() => setRmrIsOpen(false)}
                        onAnimationEnd={() => {
                            setRmrShouldNavigate(false);
                        }}
                    />
                </Popup>
            )}

            {selectedCluster && (
                <Popup
                    longitude={selectedCluster.longitude}
                    latitude={selectedCluster.latitude}
                    closeButton={false}
                    closeOnClick={false}
                    focusAfterOpen={false}
                    anchor={clusterAnchor}
                    offset={10}
                    onClose={() => setSelectedCluster(null)}
                    maxWidth="550px"
                    ref={clusterPopupRef}
                    style={{ zIndex: 1 }}
                >
                    <OrganizationClusterCard
                        entitiesInfo={selectedCluster.entitiesInfo}
                        isMixed={selectedCluster.isMixed}
                        onClose={() => setSelectedCluster(null)}
                        onOrgSelect={(id) => handleSelect(id, undefined)}
                        onCompSelect={(id) => handleSelect(undefined, id)}
                    />
                </Popup>
            )}

            {
                ((hoveredOrg && hoveredOrg.id !== selectedOrg?.id) 
                || (hoveredComp && hoveredComp.id !== selectedComp?.id) 
                || rmrIsHovered) && (
                    <Popup
                        longitude={
                            hoveredOrg ? hoveredOrg.longitude 
                            : hoveredComp ? hoveredComp!.longitude 
                            : RMR_INFO.longitude
                        }
                        latitude={
                            hoveredOrg ? hoveredOrg.latitude 
                            : hoveredComp ? hoveredComp!.latitude
                            : RMR_INFO.latitude
                        }
                        closeButton={false}
                        closeOnClick={false}
                        focusAfterOpen={false}
                        anchor="top"
                        offset={8}
                        maxWidth="308px"
                        onClose={() => setHoveredOrg(null)}
                        style={{ zIndex: 0 }}
                        className="card-fit"
                    >
                        <OrganizationName entity={hoveredOrg ?? hoveredComp ?? RMR_INFO} />
                    </Popup>
                )
            }
        </>
    );
};